<script setup lang="ts">
import NavbarSettings from "@cms/features/NavbarSettings/NavbarSettings.vue";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { openDebugTools } from "@verbleif/shared/src/components/DebugTools/useDebugTools";
import { useI18n } from "vue-i18n";
import { useNavbar } from "./useNavbar";
import Verbleif from "./verbleif_v.svg?component";

const { t, version, items, switchTheme, themeSwitchLabel } = useNavbar();
// Hyphenation needs the label language; without it a long word only wraps mid-word.
const { locale } = useI18n();
</script>

<template>
  <div
    class="max-md:hidden fixed left-0 top-0 z-5 w-[95px] h-screen flex flex-col items-center select-none py-6"
    :style="{ backgroundColor: 'var(--primary)' }"
  >
    <!-- Top: Logo -->
    <div class="mb-4" @click.shift="openDebugTools()">
      <Verbleif aria-hidden="true" />
    </div>

    <div class="border-t border-white/10 w-3/4 my-2" />

    <!-- Middle: Navigation links -->
    <nav class="flex-1 w-full flex flex-col justify-center">
      <router-link
        v-for="item in items"
        :key="item.key"
        v-slot="{ href, navigate, isActive }"
        custom
        :to="item.to"
      >
        <a
          :href="href"
          class="relative flex flex-col items-center cursor-pointer w-full py-5 gap-1.5 transition-all duration-100 hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white outline-hidden"
          :class="isActive && 'bg-white/10'"
          :aria-current="isActive ? 'page' : undefined"
          @click="navigate"
        >
          <span v-if="isActive" class="absolute left-0 top-1/2 -translate-y-1/2 h-8 w-[3px] rounded-r-full bg-white" aria-hidden="true" />
          <VIcon :name="item.icon" class="text-2xl text-white" aria-hidden="true" />
          <span class="w-full px-1 text-[11px] leading-tight text-center hyphens-auto break-normal" :lang="locale" :class="isActive ? 'text-white font-medium' : 'text-white/70'">
            {{ item.label }}
          </span>
        </a>
      </router-link>
    </nav>

    <div class="border-t border-white/10 w-3/4 my-2" />

    <!-- Bottom: Avatar, utilities, version -->
    <div class="flex flex-col items-center gap-3">
      <NavbarSettings />

      <div class="flex gap-4">
        <a
          class="cursor-pointer text-white/70 hover:text-white transition-colors duration-100 rounded-xs focus-visible:ring-2 focus-visible:ring-white outline-hidden"
          href="https://docs.verbleif.com"
          target="_blank"
          rel="noopener noreferrer"
          :aria-label="t('navbar.manual')"
          :title="t('navbar.manual')"
        >
          <VIcon class="text-sm" name="book" aria-hidden="true" />
        </a>
        <button
          type="button"
          class="theme-switch cursor-pointer text-white/70 hover:text-white transition-colors duration-100 rounded-xs focus-visible:ring-2 focus-visible:ring-white outline-hidden"
          :aria-label="themeSwitchLabel"
          :title="themeSwitchLabel"
          @click="switchTheme"
        >
          <VIcon class="text-sm icon" name="contrast" aria-hidden="true" />
        </button>
      </div>

      <div class="text-white/50 text-[10px] font-medium">
        {{ version }}
      </div>
    </div>
  </div>
</template>
